import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Header } from '../../components/layout/Header'
import { Card } from '../../components/common/Card'
import { Button } from '../../components/common/Button'
import axios from 'axios'
import { useTheme } from '../../contexts/ThemeContext'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://192.168.0.98:8090/api'

interface ComplianceStep {
  id: string
  title: string
  description: string
  status: 'completed' | 'in_progress' | 'pending' | 'not_applicable'
  required: boolean
  documents: string[]
  estimated_cost: string
  timeline: string
  ai_guidance?: string
}

interface ComplianceData {
  country: string
  region: string
  overall_score: number
  categories: {
    name: string
    score: number
    steps: ComplianceStep[]
  }[]
  ai_summary?: string
}

export const MarketCompliancePage: React.FC = () => {
  const { country } = useParams<{ country: string }>()
  const { colors } = useTheme()
  const navigate = useNavigate()
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_showAiGuidance, _setShowAiGuidance] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['market-compliance', country],
    queryFn: async () => {
      // This would normally fetch from a dedicated compliance endpoint
      // For now, we'll construct compliance data from market data
      const { data: marketData } = await axios.get(`${API_BASE_URL}/markets/${country}`)

      // Construct compliance data
      const complianceData: ComplianceData = {
        country: country || '',
        region: marketData.region || 'Europe',
        overall_score: 75,
        categories: [
          {
            name: 'VAT & Tax Registration',
            score: 80,
            steps: [
              {
                id: 'vat-1',
                title: 'Register for VAT',
                description: `Register for VAT in ${country} through the local tax authority portal`,
                status: 'pending',
                required: marketData.entry_requirements?.vat_registration ?? true,
                documents: ['VAT application form', 'Proof of business registration', 'Bank account details'],
                estimated_cost: '€0-500',
                timeline: '2-4 weeks',
                ai_guidance: `For ${country}, VAT registration is ${marketData.entry_requirements?.vat_registration ? 'required' : 'optional for low volumes'}. The process typically involves submitting documents to the local tax authority.`,
              },
              {
                id: 'vat-2',
                title: 'Set up VAT reporting',
                description: 'Configure your accounting system for VAT reporting requirements',
                status: 'pending',
                required: true,
                documents: ['VAT return template', 'Transaction records'],
                estimated_cost: '€50-200/month',
                timeline: '1 week',
              },
            ],
          },
          {
            name: 'Product Compliance',
            score: 70,
            steps: [
              {
                id: 'prod-1',
                title: 'CE Marking Verification',
                description: 'Ensure all products meet CE marking requirements',
                status: 'pending',
                required: true,
                documents: ['CE certificates', 'Technical documentation', 'Declaration of Conformity'],
                estimated_cost: '€500-5000 per product category',
                timeline: '4-12 weeks',
              },
              {
                id: 'prod-2',
                title: 'Product Labeling',
                description: 'Update product labels to meet local requirements',
                status: 'pending',
                required: true,
                documents: ['Label designs', 'Language translations'],
                estimated_cost: '€200-1000',
                timeline: '1-2 weeks',
              },
            ],
          },
          {
            name: 'Customs & Import',
            score: 85,
            steps: [
              {
                id: 'cust-1',
                title: 'EORI Number Registration',
                description: 'Obtain an Economic Operators Registration and Identification number',
                status: 'pending',
                required: true,
                documents: ['Business registration', 'VAT certificate'],
                estimated_cost: '€0',
                timeline: '1-2 weeks',
              },
              {
                id: 'cust-2',
                title: 'Customs Broker Setup',
                description: 'Establish relationship with a customs broker for import handling',
                status: 'pending',
                required: false,
                documents: ['Customs power of attorney'],
                estimated_cost: '€20-50 per shipment',
                timeline: '1 week',
              },
            ],
          },
          {
            name: 'Data Protection',
            score: 60,
            steps: [
              {
                id: 'data-1',
                title: 'GDPR Compliance Review',
                description: 'Review and update privacy policies for GDPR compliance',
                status: 'pending',
                required: true,
                documents: ['Privacy policy', 'Data processing agreements', 'Cookie policy'],
                estimated_cost: '€500-2000',
                timeline: '2-4 weeks',
              },
              {
                id: 'data-2',
                title: 'Data Processing Agreements',
                description: 'Establish DPAs with all third-party data processors',
                status: 'pending',
                required: true,
                documents: ['DPA templates', 'Vendor agreements'],
                estimated_cost: '€0-500',
                timeline: '1-2 weeks',
              },
            ],
          },
        ],
        ai_summary: `Based on your current product portfolio and ${country} market requirements, here are the key compliance priorities:\n\n1. **VAT Registration**: ${marketData.entry_requirements?.vat_registration ? 'Required before selling' : 'Consider threshold-based registration'}\n2. **Product Standards**: CE marking essential for EU markets\n3. **Customs**: EORI number needed for smooth import operations\n4. **Data Protection**: GDPR compliance mandatory for customer data handling\n\nRecommended approach: Start with VAT registration and EORI number as these are prerequisites for operations.`,
      }

      return complianceData
    },
    enabled: !!country,
  })

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
        <Header />
        <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #E0E0E0',
              borderTop: '4px solid #4A90E2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }} />
            <p style={{ color: colors.textSecondary }}>Loading compliance data...</p>
          </div>
        </main>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
        <Header />
        <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
          <Card style={{ textAlign: 'center', padding: '60px' }}>
            <p style={{ color: '#EF4444', marginBottom: '16px' }}>Error loading compliance data</p>
            <Button onClick={() => navigate('/compliance')}>Back to Compliance Center</Button>
          </Card>
        </main>
      </div>
    )
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#50C878'
      case 'in_progress': return '#4A90E2'
      case 'pending': return '#F59E0B'
      case 'not_applicable': return '#9CA3AF'
      default: return '#9CA3AF'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return 'Completed'
      case 'in_progress': return 'In Progress'
      case 'pending': return 'Pending'
      case 'not_applicable': return 'N/A'
      default: return status
    }
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      <Header />
      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <button
            onClick={() => navigate('/compliance')}
            style={{
              background: 'none',
              border: 'none',
              color: colors.textSecondary,
              cursor: 'pointer',
              fontSize: '14px',
              marginBottom: '12px',
              padding: 0,
            }}
          >
            ← Back to Compliance Center
          </button>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1 style={{ fontSize: '32px', marginBottom: '8px', color: colors.textPrimary }}>
                {data.country} Compliance
              </h1>
              <p style={{ color: colors.textSecondary, fontSize: '16px' }}>
                Step-by-step compliance guidance powered by AI
              </p>
            </div>
            <div style={{
              padding: '16px 24px',
              borderRadius: '12px',
              background: `linear-gradient(135deg, ${colors.primary}10, ${colors.primary}20)`,
              border: `1px solid ${colors.primary}40`,
              textAlign: 'center',
            }}>
              <p style={{ fontSize: '14px', color: colors.textSecondary, marginBottom: '4px' }}>Overall Score</p>
              <p style={{ fontSize: '36px', fontWeight: 700, color: colors.primary }}>{data.overall_score}%</p>
            </div>
          </div>
        </div>

        {/* Category Scores */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
          {data.categories.map((category) => (
            <Card
              key={category.name}
              style={{
                cursor: 'pointer',
                border: activeCategory === category.name ? `2px solid ${colors.primary}` : `1px solid ${colors.border}`,
                transition: 'all 0.2s',
              }}
              onClick={() => setActiveCategory(activeCategory === category.name ? null : category.name)}
            >
              <div style={{ textAlign: 'center' }}>
                <p style={{ color: colors.textSecondary, fontSize: '13px', marginBottom: '8px' }}>{category.name}</p>
                <p style={{ fontSize: '28px', fontWeight: 700, color: category.score >= 70 ? '#50C878' : category.score >= 40 ? '#F59E0B' : '#EF4444' }}>
                  {category.score}%
                </p>
                <p style={{ fontSize: '12px', color: colors.textSecondary }}>{category.steps.length} steps</p>
              </div>
            </Card>
          ))}
        </div>

        {/* AI Summary */}
        <Card style={{
          background: `linear-gradient(135deg, ${colors.cardBackground}, #7B68EE10)`,
          border: '1px solid #7B68EE40',
          marginBottom: '24px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <span style={{ fontSize: '20px' }}>🤖</span>
            <h2 style={{ fontSize: '18px', color: colors.textPrimary }}>AI Compliance Summary</h2>
          </div>
          <div style={{
            color: colors.textSecondary,
            lineHeight: 1.8,
            whiteSpace: 'pre-wrap',
          }}>
            {data.ai_summary}
          </div>
        </Card>

        {/* Compliance Steps */}
        {data.categories.map((category) => (
          <Card key={category.name} style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', color: colors.textPrimary }}>{category.name}</h2>
              <div style={{
                padding: '4px 12px',
                borderRadius: '20px',
                backgroundColor: category.score >= 70 ? '#50C87820' : category.score >= 40 ? '#F59E0B20' : '#EF444420',
                color: category.score >= 70 ? '#50C878' : category.score >= 40 ? '#F59E0B' : '#EF4444',
                fontWeight: 600,
                fontSize: '14px',
              }}>
                {category.score}% complete
              </div>
            </div>

            {category.steps.map((step, idx) => (
              <div
                key={step.id}
                style={{
                  padding: '20px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '12px',
                  marginBottom: idx < category.steps.length - 1 ? '12px' : 0,
                  backgroundColor: step.status === 'completed' ? '#50C87808' : 'transparent',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: getStatusColor(step.status) + '20',
                      color: getStatusColor(step.status),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 700,
                      flexShrink: 0,
                      marginTop: '2px',
                    }}>
                      {step.status === 'completed' ? '✓' : idx + 1}
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>{step.title}</h3>
                        {step.required && (
                          <span style={{
                            padding: '2px 6px',
                            backgroundColor: '#EF444420',
                            color: '#EF4444',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: 600,
                          }}>REQUIRED</span>
                        )}
                      </div>
                      <p style={{ color: colors.textSecondary, fontSize: '14px', lineHeight: 1.5 }}>{step.description}</p>
                    </div>
                  </div>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '20px',
                    backgroundColor: getStatusColor(step.status) + '20',
                    color: getStatusColor(step.status),
                    fontSize: '12px',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}>
                    {getStatusLabel(step.status)}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '24px', marginLeft: '36px', fontSize: '13px' }}>
                  <div>
                    <span style={{ color: colors.textSecondary }}>Timeline: </span>
                    <span style={{ color: colors.textPrimary, fontWeight: 500 }}>{step.timeline}</span>
                  </div>
                  <div>
                    <span style={{ color: colors.textSecondary }}>Est. Cost: </span>
                    <span style={{ color: colors.textPrimary, fontWeight: 500 }}>{step.estimated_cost}</span>
                  </div>
                </div>

                {step.documents.length > 0 && (
                  <div style={{ marginLeft: '36px', marginTop: '12px' }}>
                    <p style={{ fontSize: '13px', color: colors.textSecondary, marginBottom: '6px' }}>Required Documents:</p>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {step.documents.map(doc => (
                        <span key={doc} style={{
                          padding: '4px 8px',
                          backgroundColor: colors.background,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '4px',
                          fontSize: '12px',
                          color: colors.textPrimary,
                        }}>{doc}</span>
                      ))}
                    </div>
                  </div>
                )}

                {step.ai_guidance && (
                  <div style={{
                    marginLeft: '36px',
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: '#7B68EE10',
                    borderRadius: '8px',
                    borderLeft: '3px solid #7B68EE',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '14px' }}>🤖</span>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#7B68EE' }}>AI Guidance</span>
                    </div>
                    <p style={{ fontSize: '13px', color: colors.textSecondary, lineHeight: 1.5 }}>{step.ai_guidance}</p>
                  </div>
                )}
              </div>
            ))}
          </Card>
        ))}
      </main>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
